/**
 * Floating selection mini-toolbar (Canva/Figma-style) — appears near the
 * selected object with a drag handle (press-drag moves the selection,
 * bypassing the hit-test ambiguity of small / covered objects), an
 * edit-label shortcut (single selection), and an actions button (z-order /
 * lock / duplicate / delete dialog).
 *
 * Positioning tracks the selection ON THE UI THREAD: a `useAnimatedStyle`
 * worklet reads the camera `{tx,ty,zoom}` + live-drag `dragDx/dragDy`
 * shared values and converts the closure-captured world-coords
 * `selectionFrame` to screen coords per frame — zero React re-renders
 * during pan/zoom/drag (the engine's SelectionPlane contract). The frame
 * itself refreshes through the dep array on every commit. Anchored above
 * the selection top-center (clearing the rotate handle), flipping below
 * near the top edge and clamping to the viewport. Hidden (opacity) during
 * resize/rotate/marquee/draw, when the worklet can't cheaply mirror the
 * changing geometry; reappears on settle.
 *
 * @module FloorPlanEditorSelectionToolbar
 * @category Containers
 */

import { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { PanGesture } from 'react-native-gesture-handler';
import type { SharedValue } from 'react-native-reanimated';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { Ellipsis, Pencil, RotateCw } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { LayoutChangeEvent } from 'react-native';

import FloorPlanToolButton from '@containers/Store/FloorPlan/Editor/Toolbar/ToolButton';

import SelectionToolbarDragHandle from './DragHandle';

import type { FloorCamera } from '@src/floorplan';
import { FLOOR_DRAG_MODE } from '@src/floorplan';
import type { Bounds, ViewportSize } from '@src/floorplan/models/geometry';

/** Clears the rotate handle: 28px offset + 22px half-touch + margin. */
const GAP_ABOVE_PX = 58;
/** Clears the south resize handle's half-touch when flipped below. */
const GAP_BELOW_PX = 24;
const EDGE_PX = 8;

type CardSize = {
  width: number;
  height: number;
};

type Props = {
  /** Engine camera shared values (UI-thread positioning input). */
  camera: FloorCamera;
  /** Live object-drag offsets (world units) — the toolbar follows a drag. */
  dragDx: SharedValue<number>;
  dragDy: SharedValue<number>;
  /** Engine drag mode (FLOOR_DRAG_MODE values) — drives visibility. */
  dragMode: SharedValue<number>;
  /** Selection union AABB in WORLD coords (recomputed per commit). */
  selectionFrame: Bounds | null;
  /** Canvas layout size (screen px) for edge clamping. */
  viewport: ViewportSize;
  /** Prebuilt drag-handle pan from the engine. */
  selectionDragGesture: PanGesture;
  /** Render the edit-label shortcut (single selection only). */
  showEdit: boolean;
  /** Screen-px clearance under the top toolbar strip for the flip logic. */
  topInset: number;
  onEdit: () => void;
  /** Tap-to-rotate 90° (the touch-friendly rotate; handle stays for freeform). */
  onRotate: () => void;
  onOpenActions: () => void;
};

/**
 * Floating selection mini-toolbar (Canva/Figma-style) rendered near the
 * selected object — drag handle, rotate, edit-label (single selection), and
 * an actions button. Positioned on the UI thread via `useAnimatedStyle`.
 *
 * @category Containers
 * @param camera - Engine camera shared values (UI-thread positioning input).
 * @param dragDx - Live object-drag X offset (world units).
 * @param dragDy - Live object-drag Y offset (world units).
 * @param dragMode - Engine drag mode — drives visibility.
 * @param selectionFrame - Selection union AABB in world coords.
 * @param viewport - Canvas layout size (screen px) for edge clamping.
 * @param selectionDragGesture - Prebuilt drag-handle pan from the engine.
 * @param showEdit - Render the edit-label shortcut (single selection only).
 * @param topInset - Screen-px clearance under the top toolbar strip.
 * @param onEdit - Fired when the edit-label shortcut is pressed.
 * @param onRotate - Fired to rotate the selection 90°.
 * @param onOpenActions - Fired to open the selection actions dialog.
 */
const FloorPlanSelectionToolbar = ({
  camera,
  dragDx,
  dragDy,
  dragMode,
  selectionFrame,
  viewport,
  selectionDragGesture,
  showEdit,
  topInset,
  onEdit,
  onRotate,
  onOpenActions,
}: Props) => {
  const { t } = useTranslation();
  // Measured card size — the worklet needs it to center/clamp, and the
  // width changes when the edit button toggles with the selection count.
  const [size, setSize] = useState<CardSize | null>(null);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setSize({ width, height });
  }, []);

  const animatedStyle = useAnimatedStyle(() => {
    if (selectionFrame === null || size === null) {
      return { opacity: 0 };
    }
    const zoom = camera.zoom.get();
    const dx = dragDx.get();
    const dy = dragDy.get();
    const centerX =
      (selectionFrame.x + selectionFrame.width / 2 + dx) * zoom +
      camera.tx.get();
    const topPx = (selectionFrame.y + dy) * zoom + camera.ty.get();
    const bottomPx =
      (selectionFrame.y + selectionFrame.height + dy) * zoom + camera.ty.get();
    // Above the selection, clearing the rotate handle; flip below near the
    // top edge; clamp to the viewport either way.
    let y = topPx - GAP_ABOVE_PX - size.height;
    if (y < topInset + EDGE_PX) y = bottomPx + GAP_BELOW_PX;
    if (y > viewport.height - size.height - EDGE_PX) {
      y = viewport.height - size.height - EDGE_PX;
    }
    if (y < topInset + EDGE_PX) y = topInset + EDGE_PX;
    let x = centerX - size.width / 2;
    if (x < EDGE_PX) x = EDGE_PX;
    if (x > viewport.width - size.width - EDGE_PX) {
      x = viewport.width - size.width - EDGE_PX;
    }
    const currentMode = dragMode.get();
    const visible =
      currentMode === FLOOR_DRAG_MODE.PENDING ||
      currentMode === FLOOR_DRAG_MODE.OBJECT ||
      currentMode === FLOOR_DRAG_MODE.CAMERA;
    return {
      opacity: visible ? 1 : 0,
      transform: [{ translateX: x }, { translateY: y }],
    };
  });

  return (
    <Animated.View
      pointerEvents="box-none"
      style={[styles.host, animatedStyle]}>
      <View
        onLayout={handleLayout}
        className="bg-card/95 border-border flex-row items-center gap-1 rounded-2xl border p-1 shadow-lg shadow-black/25 backdrop-blur">
        <SelectionToolbarDragHandle
          gesture={selectionDragGesture}
          label={t('pages:floorPlanEditor.selectionToolbar.drag')}
        />
        <FloorPlanToolButton
          icon={RotateCw}
          label={t('pages:floorPlanEditor.selectionToolbar.rotate')}
          onPress={onRotate}
        />
        {showEdit ? (
          <FloorPlanToolButton
            icon={Pencil}
            label={t('pages:floorPlanEditor.selectionToolbar.edit')}
            onPress={onEdit}
          />
        ) : null}
        <FloorPlanToolButton
          icon={Ellipsis}
          label={t('pages:floorPlanEditor.selectionToolbar.actions')}
          onPress={onOpenActions}
        />
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  host: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
});

export default FloorPlanSelectionToolbar;
